import { Component, Event, EventEmitter, h, Listen, Prop, State } from '@stencil/core';
import { FacetChangeEvent, Option, FacetUpdateEvent, facetChangeValueHandler, getData } from '@hpfc/fc-frontend-core';

/**
 * Dropdown select box with a label used for selecting sort options.
 */
@Component({
  tag: 'fccd-sort-by-v2',
  styleUrl: 'sort-by-v2.scss',
})
export class SortByV2 {
  /**
   * The id for the element in this component - must match the desired name of the facet or URL param used for sorting.
   */
  @Prop() elementId!: string;

  /**
   * JSON string with an array of sort options (`Array<Option>`).
   */
  @Prop() optionsJson: string;

  /**
   * Label for the sort dropdown menu.
   */
  @Prop() panelLabel: string;

  @State() sortByData: Array<Option>;
  @State() selectedIndex: number = null;

  /**
   * Dispatches options when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  componentWillLoad() {
    if (!this.sortByData) {
      this.sortByData = getData(this.optionsJson);
    }
  }

  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    const facet = event.detail.facetMap.get(this.elementId);

    const sortOptionArray = event.detail.facetOptionMap.get(this.elementId);
    if (sortOptionArray && (!this.sortByData || this.isDifferent(sortOptionArray))) {
      this.sortByData = sortOptionArray.map(o => new Option(o.name, o.value));
    }

    if (!this.sortByData || !facet) {
      return;
    }

    this.sortByData.forEach((d, index) => {
      if (facet.value === d.value) {
        d.isSelected = true;
        this.selectedIndex = index;
      } else {
        d.isSelected = false;
      }
      return d;
    });
  }

  private isDifferent(sortOptionArray: Array<Option>) {
    if (this.sortByData.length != sortOptionArray.length) {
      return true;
    }

    const sortOptionSet = new Set(sortOptionArray.map(o => o.value));
    return this.sortByData.some(o => !sortOptionSet.has(o.value));
  }

  @Listen('hp5ValidationChange')
  handleTextSelectEvent(event: CustomEvent) {
    if (this.selectedIndex != null && event.detail.value === this.sortByData[this.selectedIndex].value) {
      return;
    }

    facetChangeValueHandler(this.fccdFacetChangeEvent, event, this.elementId, event.detail.value, true);
  }

  private getPanelName(): string {
    return `sort-by-${this.elementId}`;
  }

  render() {
    return (
      <div class="inline">
        <hp5-heading size-selection="xs" tag-selection="h4">
          {this.panelLabel}
        </hp5-heading>
        <hp5-select
          name={this.getPanelName()}
          element-id={this.getPanelName()}
          label={this.panelLabel}
          options={JSON.stringify(this.sortByData)}
          required="false"
          hide-input-optional-label="true"
        />
      </div>
    );
  }
}
